import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, MailCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { isAdmin } from "../../utils/role";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { deleteBranch, requestBranchDeleteCode } from "../../api/directory";

// حذف الفرع لمدير الشركة فقط، ولا يتم إلا برمز تأكيد يصل إلى بريد مدير الشركة المسجّل.
export const BranchDangerZone = ({ branch }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("idle");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!isAdmin(user)) {
    return (
      <NeuCard className="border border-destructive/30" data-testid="branch-danger-zone">
        <p className="label-xs text-destructive">{t("branches.dangerZone")}</p>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="branch-delete-admin-only">
          {t("branches.adminOnlyDelete")}
        </p>
      </NeuCard>
    );
  }

  const sendCode = async () => {
    setBusy(true);
    try {
      const res = await requestBranchDeleteCode(branch.id);
      setSentTo(res.email || "");
      setTestCode(res.test_code || "");
      setStage("code");
      toast.success(t("branches.deleteCodeSent", { email: res.email }));
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteBranch(branch.id, code.trim());
      toast.success(t("branches.deleted"));
      navigate("/branches");
    } catch {
      toast.error(t("branches.deleteCodeError"));
      setBusy(false);
    }
  };

  return (
    <NeuCard className="border border-destructive/30" data-testid="branch-danger-zone">
      <p className="label-xs text-destructive">{t("branches.dangerZone")}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t("branches.deleteHint")}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t("branches.deleteNeedsCode")}</p>

      {stage === "idle" ? (
        <NeuButton variant="danger" className="mt-4 flex items-center gap-2 px-4 py-2"
          data-testid="branch-delete" disabled={busy} onClick={sendCode}>
          <Trash2 className="h-4 w-4" /> {t("branches.deleteBranch")}
        </NeuButton>
      ) : (
        <div className="mt-4 grid gap-3 sm:max-w-md" data-testid="branch-delete-confirm">
          <p className="flex items-center gap-2 text-xs font-bold text-primary">
            <MailCheck className="h-4 w-4" /> {t("branches.deleteCodeSent", { email: sentTo })}
          </p>
          {testCode ? (
            <p className="font-mono text-[11px] text-muted-foreground" data-testid="branch-delete-test-code">
              {t("branches.testCodeNote", { code: testCode })}
            </p>
          ) : null}
          <NeuInput data-testid="branch-delete-code" inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder={t("branches.deleteCodePlaceholder")} />
          <div className="flex flex-wrap gap-2">
            <NeuButton variant="danger" className="px-4 py-2" data-testid="branch-delete-confirm-btn"
              disabled={busy || code.length !== 6} onClick={remove}>
              {busy ? t("app.saving") : t("branches.deleteConfirm")}
            </NeuButton>
            <NeuButton className="px-4 py-2" data-testid="branch-delete-resend" disabled={busy} onClick={sendCode}>
              {t("branches.resendDeleteCode")}
            </NeuButton>
            <NeuButton className="px-4 py-2" data-testid="branch-delete-cancel" onClick={() => setStage("idle")}>
              {t("app.cancel")}
            </NeuButton>
          </div>
        </div>
      )}
    </NeuCard>
  );
};
